import React from 'react';

import {
    Box,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TableRow,
    TableSortLabel
} from '@mui/material';

import { MayBeNil } from 'app/@types/utils/may-be-nil';
import Paginator from 'pages/Search/components/Paginator';

import SortedIcon from './assets/sorted.svg';
import UnsortedIcon from './assets/unsorted.svg';
import useStyles from './styles';

interface Header<F extends string> {
    field: F;
    label: React.ReactNode;
    sort?: boolean;
    width?: React.CSSProperties['width'];
}

type Direction = 'asc' | 'desc';

interface Sorting<F extends string> {
    field: F;
    direction: Direction;
}

interface Props<F extends string> {
    headers: Header<F>[];
    data: React.ReactNode[][];
    page?: number;
    pageSize?: number;
    totalCount?: number;
    sorting?: Sorting<F>;
    onPageChange?: (page: number) => void;
    onPageSizeChange?: (size?: number) => void;
    getPaginationHref?: (page: MayBeNil<number>) => string;
    onSortingChange?: (value: Sorting<F>) => void;
    loading?: boolean;
}

const COUNTER_DIRECTIONS = { asc: 'desc', desc: 'asc' } as const;

function DataTable<F extends string>({
    headers,
    data,
    page = 1,
    pageSize = 100,
    totalCount = -1,
    sorting,
    onPageChange,
    onPageSizeChange,
    getPaginationHref,
    onSortingChange,
    loading
}: Props<F>) {
    const { classes } = useStyles();

    return (
        <TableContainer className={classes.container}>
            <Table>
                <TableHead>
                    <TableRow>
                        {headers.map((header) => (
                            <TableCell key={header.field} className={classes.headerCell} width={header.width}>
                                <TableSortLabel
                                    disabled={!header.sort}
                                    hideSortIcon={!header.sort}
                                    IconComponent={sorting?.field === header.field ? SortedIcon : UnsortedIcon}
                                    active={!!header.sort}
                                    direction={
                                        sorting?.field === header.field ? COUNTER_DIRECTIONS[sorting.direction] : 'desc'
                                    }
                                    onClick={() =>
                                        onSortingChange?.({
                                            field: header.field,
                                            direction:
                                                sorting?.field === header.field
                                                    ? COUNTER_DIRECTIONS[sorting.direction]
                                                    : 'asc'
                                        })
                                    }
                                    classes={{ root: classes.sort, icon: classes.sortIcon }}
                                >
                                    {header.label}
                                </TableSortLabel>
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {loading ? (
                        <TableRow>
                            <TableCell colSpan={1000}>
                                <Box className={classes.spinner}>
                                    <CircularProgress />
                                </Box>
                            </TableCell>
                        </TableRow>
                    ) : (
                        data.map((cells, index) => (
                            <TableRow key={index} hover>
                                {cells.map((value, index) => (
                                    <TableCell key={index} className={classes.bodyCell}>
                                        {value}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))
                    )}
                </TableBody>
                <TableFooter>
                    <TableRow>
                        <td colSpan={headers.length || 1} className={classes.footerCell}>
                            <Paginator
                                page={page}
                                rowsPerPage={pageSize}
                                count={totalCount}
                                onPageChange={onPageChange}
                                onRowsPerPageChange={onPageSizeChange}
                                getPaginationHref={getPaginationHref}
                            />
                        </td>
                    </TableRow>
                </TableFooter>
            </Table>
        </TableContainer>
    );
}

export default DataTable;
